import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useBrand } from "@/contexts/BrandContext";

export type AboutInfo = {
  story: string;
  storyAr: string;
  mission: string;
  missionAr: string;
  contactPhone: string;
  contactEmail: string;
  contactAddress: string;
  contactAddressAr: string;
};

export type TeamMember = {
  id: string;
  name: string;
  nameAr: string;
  role: string;
  roleAr: string;
  photoUrl: string | null;
};

// ─── Module-level cache ───────────────────────────────────────────────────────
// Shared across ALL component instances so only one fetch ever runs per brand id.
type CacheEntry = {
  about: AboutInfo | null;
  team: TeamMember[];
};

const cache = new Map<string, CacheEntry>();
// Keeps the in-flight promise so concurrent mounts share a single request.
const inflight = new Map<string, Promise<CacheEntry>>();

async function fetchAboutData(brandId: string): Promise<CacheEntry> {
  if (inflight.has(brandId)) return inflight.get(brandId)!;

  const promise = (async () => {
    const [aboutRes, teamRes] = await Promise.all([
      supabase.from("restaurant_about").select("*").eq("restaurant_id", brandId).maybeSingle(),
      supabase.from("team_members").select("*").eq("restaurant_id", brandId).order("sort_order"),
    ]);

    let about: AboutInfo | null = null;
    if (aboutRes.data) {
      const d = aboutRes.data as any;
      about = {
        story: d.story || "",
        storyAr: d.story_ar || "",
        mission: d.mission || "",
        missionAr: d.mission_ar || "",
        contactPhone: d.contact_phone || "",
        contactEmail: d.contact_email || "",
        contactAddress: d.contact_address || "",
        contactAddressAr: d.contact_address_ar || "",
      };
    }

    const team: TeamMember[] = (teamRes.data || []).map((m: any) => ({
      id: m.id,
      name: m.name,
      nameAr: m.name_ar || "",
      role: m.role || "",
      roleAr: m.role_ar || "",
      photoUrl: m.photo_url,
    }));

    const entry: CacheEntry = { about, team };
    cache.set(brandId, entry);
    inflight.delete(brandId);
    return entry;
  })();

  inflight.set(brandId, promise);
  return promise;
}
// ─────────────────────────────────────────────────────────────────────────────

export function usePublicAbout() {
  const { brand } = useBrand();

  const cached = brand.id ? cache.get(brand.id) : undefined;
  const [about, setAbout] = useState<AboutInfo | null>(cached?.about ?? null);
  const [team, setTeam] = useState<TeamMember[]>(cached?.team ?? []);
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    if (!brand.id) return;
    // Data already in cache — nothing to do
    if (cache.has(brand.id)) return;

    let cancelled = false;
    fetchAboutData(brand.id).then((entry) => {
      if (cancelled) return;
      setAbout(entry.about);
      setTeam(entry.team);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [brand.id]);

  return { about, team, loading };
}
